/**
 * Mobile entry point — booking.rsmaidsservice.net + Capacitor native
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { setupIonicReact } from '@ionic/react';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import '../i18n';
import App from '../App';
import { registerSW } from 'virtual:pwa-register';
import { horizontalTransition } from '../theme/horizontalTransition';
import { preventAriaFocusIssue } from '@rsmaids/shared/utils/ariaFix';
import { initializeServiceWorkerForNotifications } from '../utils/notificationUtils';
import { initializeForegroundNotifications } from '../utils/foregroundNotifications';
import { auth } from '../auth/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { StaffCacheService } from '@rsmaids/shared/services/api/staffCacheService';
import { prewarmAgendaWindow } from '@rsmaids/shared/services/api/bookingsCacheService';
import { proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { installFocusAboveKeyboard } from '../utils/focusAboveKeyboard';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Dark mode */
import '@ionic/react/css/palettes/dark.class.css';

/* Global styles */
import '../theme/global.scss';

/* Theme variables */
import '../theme/variables.scss';


// Set up Ionic React with platform-adaptive mode and keyboard settings.
// iOS needs Ionic's ios mode so toolbars/header content respect the status-bar safe area.
const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);

setupIonicReact({
  mode: isIOSDevice ? 'ios' : 'md',
  animated: true,
  swipeBackEnabled: false,
  navAnimation: horizontalTransition,
  hardwareBackButton: true,
  experimentalCloseWatcher: true,

});

// Fix iOS 26 PWA standalone detection.
{
  const nav = window.navigator as { standalone?: boolean };
  const isStandalone =
    nav.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches ||
    new URLSearchParams(window.location.search).get('mode') === 'standalone';

  if (isStandalone) {
    const patchPlatformClasses = () => {
      const html = document.documentElement;
      if (!html.classList.contains('plt-pwa')) {
        html.classList.add('plt-pwa');
      }
      html.classList.remove('plt-mobileweb');
    };
    patchPlatformClasses();
    requestAnimationFrame(patchPlatformClasses);
    setTimeout(patchPlatformClasses, 300);
    setTimeout(patchPlatformClasses, 1000);
  }
}

preventAriaFocusIssue();

const LIGHT_THEME_COLOR = '#f5f7fb';
const DARK_THEME_COLOR = '#0f1729';

const updateThemeColorMeta = (isDark: boolean) => {
  const themeColor = isDark ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
  const themeMetaTags = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  themeMetaTags.forEach((tag) => {
    if (!tag.media || tag.media === 'all') {
      tag.setAttribute('content', themeColor);
    }
  });
};

const _isAndroidNative = !!(window as unknown as { androidBridge?: unknown }).androidBridge;
const _isIOSNative = !!(window as unknown as { webkit?: { messageHandlers?: { bridge?: unknown } } }).webkit?.messageHandlers?.bridge;
const _isNative = _isAndroidNative || _isIOSNative;

console.log('[SafeArea] Bridge detection:', { android: _isAndroidNative, ios: _isIOSNative });

if (_isNative) {
  document.documentElement.classList.add('capacitor-native');
  if (_isAndroidNative) {
    document.documentElement.classList.add('capacitor-android');
  }
  if (_isIOSNative) {
    document.documentElement.classList.add('capacitor-ios');
  }

  if (_isAndroidNative) {
    // Capacitor 8.3.x + Android 15/16 mandatory edge-to-edge handles insets natively.
    // Map CSS env() safe area values to Ionic's --ion-safe-area-* variables.
    const root = document.documentElement;
    root.style.setProperty('--ion-safe-area-top',    'env(safe-area-inset-top, 0px)');
    root.style.setProperty('--ion-safe-area-bottom', 'env(safe-area-inset-bottom, 0px)');
    root.style.setProperty('--ion-safe-area-left',   'env(safe-area-inset-left, 0px)');
    root.style.setProperty('--ion-safe-area-right',  'env(safe-area-inset-right, 0px)');
  }

  // Passkeys in the installed app: route the browser's WebAuthn calls to the phone's own passkey dialog
  import('@capgo/capacitor-passkey')
    .then(({ CapacitorPasskey }) => CapacitorPasskey.autoShimWebAuthn())
    .catch((err) => console.warn('[Passkey] native shim not available:', err));

  import('@capacitor/core').then(({ SystemBars, SystemBarsStyle }) => {
    const isDark = document.documentElement.classList.contains('dark');
    SystemBars.setStyle({ style: isDark ? SystemBarsStyle.Dark : SystemBarsStyle.Light }).catch(console.warn);
    (window as unknown as { __capacitorSystemBars?: unknown }).__capacitorSystemBars = { SystemBars, SystemBarsStyle };
  }).catch(console.warn);

  let appReady = false;
  let splashHidden = false;

  const hideNativeSplash = () => {
    if (splashHidden || !appReady) return;
    splashHidden = true;
    import('@capacitor/splash-screen')
      .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 400 }))
      .catch(console.warn);
  };

  const fallbackTimer = setTimeout(() => {
    if (!splashHidden) {
      appReady = true;
      document.getElementById('root')?.classList.add('app-show');
      requestAnimationFrame(() => requestAnimationFrame(hideNativeSplash));
    }
  }, 4000);

  window.addEventListener('rsmaids-app-ready', () => {
    clearTimeout(fallbackTimer);
    appReady = true;
    document.getElementById('root')?.classList.add('app-show');
    requestAnimationFrame(() => requestAnimationFrame(hideNativeSplash));
  }, { once: true });
}

async function initializeNotifications() {
  try {
    initializeServiceWorkerForNotifications();
    console.log('[Main] Service worker initialized');
  } catch (error) {
    console.error('[Main] Error initializing service worker:', error);
  }

  try {
    initializeForegroundNotifications();
    console.log('[Main] Foreground notifications initialized');
  } catch (error) {
    console.error('[Main] Error initializing foreground notifications:', error);
  }

  try {
    console.log('[Staff] Waiting for auth to initialize staff cache');
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const staffCache = StaffCacheService.getInstance();
          await staffCache.loadStaffCache();
          staffCache.subscribeToStaffUpdates();
          // Warm the browser's image cache with every staff avatar now, so a conversation opens
          // with them already downloaded instead of each image arriving after the messages render.
          staffCache.getAllStaff().forEach((staff) => {
            const url = proxyImage(staff.contactAvatar || staff.mainMedia);
            if (url) new Image().src = url;
          });
          console.log('[Staff] Staff cache initialized and subscribed to updates');
        } catch (error) {
          console.error('[Staff] Error initializing staff cache:', error);
        }
      }
    });
  } catch (error) {
    console.error('[Staff] Error setting up staff cache:', error);
  }

  try {
    // Start the agenda's data now, while the app is still launching, so it is
    // already loading by the time the schedule page renders.
    const stopPrewarmAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        stopPrewarmAuth();
        prewarmAgendaWindow();
      }
    });
  } catch (error) {
    console.error('[Agenda] Error starting agenda prewarm:', error);
  }

  if ('Notification' in window) {
    console.log('[Main] Notification permission state:', Notification.permission);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeNotifications);
} else {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initializeNotifications);
  } else {
    setTimeout(initializeNotifications, 0);
  }
}

// Native only: scroll any focused text field above the on-screen keyboard (resize:none mode)
installFocusAboveKeyboard();

// iOS-specific: Prevent viewport scrolling for standalone PWAs on iOS only
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

if (isIOS) {
  window.addEventListener('touchmove', (e) => {
    const target = e.target as HTMLElement;
    const ionContent = target.closest('ion-content');
    if (!ionContent) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }, { passive: false, capture: true });

  const TEXT_INPUT_TYPES = new Set(['text', 'email', 'password', 'search', 'tel', 'url', 'number', 'date', 'time', '']);
  document.addEventListener('touchend', (e) => {
    const el = e.target as HTMLElement;
    const tag = el.tagName;
    if (tag === 'TEXTAREA' || (tag === 'INPUT' && TEXT_INPUT_TYPES.has((el as HTMLInputElement).type?.toLowerCase() ?? ''))) {
      e.preventDefault();
      (el as HTMLInputElement).focus({ preventScroll: true });
    }
  }, { capture: true, passive: false });

  document.addEventListener('focus', (e) => {
    const el = e.target as HTMLElement;
    const tag = el.tagName;
    if (tag === 'TEXTAREA' || (tag === 'INPUT' && TEXT_INPUT_TYPES.has((el as HTMLInputElement).type?.toLowerCase() ?? ''))) {
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  }, { capture: true, passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      setTimeout(() => window.scrollTo(0, 0), 150);
    }
  });
}

// Global theme management
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('theme') || 'system';
  applyTheme(savedTheme);
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const currentTheme = localStorage.getItem('theme') || 'system';
    if (currentTheme === 'system') applyTheme('system');
  });
};

const applyTheme = (theme: string) => {
  const html = document.documentElement;
  const body = document.body;
  let isDark = false;

  if (theme === 'dark') {
    html.classList.add('dark');
    body.classList.remove('dark');
    isDark = true;
  } else if (theme === 'light') {
    html.classList.remove('dark');
    body.classList.remove('dark');
    isDark = false;
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      html.classList.add('dark');
      body.classList.remove('dark');
      isDark = true;
    } else {
      html.classList.remove('dark');
      body.classList.remove('dark');
      isDark = false;
    }
  }
  const sb = (window as unknown as { __capacitorSystemBars?: { SystemBars: { setStyle: (options: { style: string }) => Promise<void> }; SystemBarsStyle: { Dark: string; Light: string } } }).__capacitorSystemBars;
  if (sb) {
    sb.SystemBars.setStyle({ style: isDark ? sb.SystemBarsStyle.Dark : sb.SystemBarsStyle.Light }).catch(console.warn);
  }
  const na = (window as unknown as { NativeAndroid?: { updateScrimTheme: (isDark: boolean) => void } }).NativeAndroid;
  if (na) na.updateScrimTheme(isDark);

  updateThemeColorMeta(isDark);
};

declare global {
  interface Window {
    applyTheme: (theme: string) => void;
  }
}

window.applyTheme = applyTheme;
initializeTheme();

if (import.meta.env.DEV) {
  const SW_IMPORT_RECOVERY_KEY = 'sw-dynamic-import-recovered';
  window.addEventListener('error', (event) => {
    if (!('message' in event) || typeof event.message !== 'string') return;
    if (!event.message.includes('Failed to fetch dynamically imported module')) return;
    if (sessionStorage.getItem(SW_IMPORT_RECOVERY_KEY)) return;

    // Dev SW can occasionally hold stale module URLs after optimizer changes.
    // One forced reload recovers while keeping localhost SW enabled.
    sessionStorage.setItem(SW_IMPORT_RECOVERY_KEY, '1');
    window.location.reload();
  });
}

if ('serviceWorker' in navigator) {
  registerSW({
    immediate: true,
    onNeedReload() {
      // In autoUpdate mode VitePWA calls this instead of window.location.reload().
      // Suppress the reload while an OAuth popup is open — the popup activating a new SW
      // version would otherwise crash the main window mid-auth.
      if (sessionStorage.getItem('outlookAuthInProgress')) return;
      window.location.reload();
    },
    onNeedRefresh() {
      console.log('App updated and ready');
    },
    onOfflineReady() {
      console.log('App ready to work offline');
    },
  });
}

console.log('[PWA Elements] Starting defineCustomElements...');
const pwaElementsReady = defineCustomElements(window);

const container = document.getElementById('root');
const root = createRoot(container!);

const renderApp = () => {
  root.render(
    <React.Fragment>
      <App />
    </React.Fragment>
  );
};

pwaElementsReady.then(async () => {
  console.log('[PWA Elements] defineCustomElements completed');
  try {
    const testModal = document.createElement('pwa-camera-modal');
    console.log('[PWA Elements] Created test pwa-camera-modal, constructor:', testModal.constructor.name);
    if (customElements.whenDefined) {
      await Promise.all([
        customElements.whenDefined('pwa-camera-modal'),
        customElements.whenDefined('pwa-action-sheet'),
        customElements.whenDefined('pwa-toast'),
      ]);
      console.log('[PWA Elements] All elements fully defined via whenDefined');
    }
  } catch (err) {
    console.error('[PWA Elements] Error forcing element registration:', err);
  }
  renderApp();
}).catch((err) => {
  console.error('[PWA Elements] Failed to register:', err);
  renderApp();
});

// Fade out splash and fade in app when ready
window.addEventListener('DOMContentLoaded', () => {
  if (_isNative) return;
  if (window.location.pathname === '/google-calendar-callback') {
    document.getElementById('custom-splash')?.classList.add('splash-hide');
    document.getElementById('root')?.classList.add('app-show');
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const isStandalone = urlParams.get('mode') === 'standalone' ||
    window.matchMedia('(display-mode: standalone)').matches ||
    'standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true;

  const splashDuration = isStandalone ? 800 : 1200;

  setTimeout(() => {
    document.getElementById('custom-splash')?.classList.add('splash-hide');
    document.getElementById('root')?.classList.add('app-show');
    const na = (window as unknown as { NativeAndroid?: { setScrimVisible: (v: boolean) => void } }).NativeAndroid;
    if (na && !document.body.classList.contains('login-route')) {
      na.setScrimVisible(true);
    }
  }, splashDuration);
});
